import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";

import type { FC } from "react";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";

import defaultConfig from "@config";
import {
	faDiscord,
	faTwitter,
	faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useMediaQuery from "@hooks/useMediaQuery";
import type { MyLocale } from "@i18n";
import { Whatshot } from "@mui/icons-material";
import { useI18n } from "next-rosetta";
import {
	AiOutlineUserAdd as AddUser,
	AiOutlineBank as Bank,
	AiOutlineDashboard as DashboardDots,
} from "react-icons/ai";
import { BsNewspaper as GoogleDocs, BsPeople as Group } from "react-icons/bs";
import { FiServer as Server } from "react-icons/fi";
import { PiHandbagSimpleBold as Bag } from "react-icons/pi";

const drawerWidth = 280;
const AUTO_REMOVE_REFFERAL_NEW_TAG = new Date("2022-03-15").getTime();
const AUTO_REMOVE_SWAGSHOP_NEW_TAG = new Date("2022-04-15").getTime();

interface SidebarProps {
	open?: boolean;
	// eslint-disable-next-line no-unused-vars
	toggleOpen: (state: boolean) => void;
}

const Sidebar: FC<SidebarProps> = ({ open, toggleOpen }) => {
	const i18n = useI18n<MyLocale>();
	const { pathname } = useRouter();
	const { isLargeOrLower: isMobile } = useMediaQuery();

	return (
		<Drawer
			sx={{
				"& .MuiDrawer-paper": {
					backgroundColor: "rgba(0, 0, 0, 0.5)",
					border: "none",
					overflowX: "hidden",
					width: drawerWidth,
				},
				flexShrink: 0,
				width: drawerWidth,
			}}
			variant={isMobile ? "temporary" : "permanent"}
			anchor="left"
			open={open}
			onClose={() => toggleOpen(!open)}
			style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", border: "none" }}
		>
			<Box
				sx={{
					backgroundColor: "rgba(0, 0, 0, 0.5)",
					width: 280,
				}}
			>
				<Container
					sx={{
						backgroundColor: "rgba(0, 0, 0, 0.5)",
						display: "flex",
						justifyContent: "center",
						paddingTop: 4,
					}}
				>
					<Image
						src="/assets/vapornodes-new-logo.png"
						alt="logo"
						height={20}
						width={230}
					/>
				</Container>
			</Box>
			<Box
				sx={{
					backgroundColor: "rgba(0, 0, 0, 0.5)",
					display: "flex",
					flexDirection: "column",
					flexGrow: 1,
					paddingTop: 6,
				}}
			>
				<List
					sx={{
						"& .Mui-selected": {
							backgroundColor: "transparent",
							color: "#14D3ED",
						},
						"& .MuiListItem": {
							backgroundColor: "transparent",
						},
						backgroundColor: "rgba(0, 0, 0, 0.5)",
					}}
				>
					<a
						href={defaultConfig.vaporDEX.app}
						target="_blank"
						rel="noopener noreferrer"
						style={{ color: "white", textDecoration: "none" }}
					>
						<ListItem button onClick={() => toggleOpen(!open)}>
							<ListItemIcon>
								<Whatshot />
							</ListItemIcon>
							<ListItemText>VaporDEX</ListItemText>
						</ListItem>
					</a>

					<Link
						href="/"
						passHref
						style={{ color: "white", textDecoration: "none" }}
						legacyBehavior
					>
						<ListItem
							button
							selected={pathname === "/"}
							onClick={() => toggleOpen(!open)}
						>
							<ListItemIcon
								sx={{ color: pathname === "/" ? "#14D3ED" : undefined }}
							>
								<DashboardDots size={30} />
							</ListItemIcon>
							<ListItemText>{i18n.t("sidebar.dashboard")}</ListItemText>
						</ListItem>
					</Link>

					<Link
						href="/nodes"
						passHref
						style={{ color: "white", textDecoration: "none" }}
						legacyBehavior
					>
						<ListItem
							button
							selected={pathname === "/nodes"}
							onClick={() => toggleOpen(!open)}
						>
							<ListItemIcon
								sx={{ color: pathname === "/nodes" ? "#14D3ED" : undefined }}
							>
								<Server size={30} />
							</ListItemIcon>
							<ListItemText>{i18n.t("sidebar.nodes")}</ListItemText>
						</ListItem>
					</Link>

					<Link href="/referral" passHref legacyBehavior>
						<ListItem
							button
							selected={pathname === "/referral"}
							onClick={() => toggleOpen(!open)}
							style={{ color: "white", textDecoration: "none" }}
						>
							<ListItemIcon
								sx={{ color: pathname === "/referral" ? "#14D3ED" : undefined }}
							>
								<AddUser size={30} />
							</ListItemIcon>
							<ListItemText>
								{i18n.t("sidebar.referral")}
								{AUTO_REMOVE_REFFERAL_NEW_TAG - Date.now() >= 0 && (
									<Chip
										label="new"
										color="primary"
										size="small"
										sx={{ marginLeft: 2 }}
									/>
								)}
							</ListItemText>
						</ListItem>
					</Link>
				</List>

				<Divider />

				<Box
					sx={{
						backgroundColor: "rgba(0, 0, 0, 0.5)",
						display: "flex",
						flexDirection: "column",
						flexGrow: 1,
						justifyContent: "space-between",
					}}
				>
					<List sx={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}>
						<ListItemButton
							component="a"
							href="https://docs.vapornodes.finance"
							target="_blank"
							rel="noreferrer"
							disableRipple
						>
							<ListItemIcon>
								<GoogleDocs size={30} />
							</ListItemIcon>
							<ListItemText>{i18n.t("sidebar.documentation")}</ListItemText>
						</ListItemButton>
						<ListItemButton
							component="a"
							href="https://snapshot.org/#/vapornodes.eth"
							target="_blank"
							rel="noreferrer"
						>
							<ListItemIcon>
								<Group size={30} />
							</ListItemIcon>
							<ListItemText>{i18n.t("sidebar.governance")}</ListItemText>
						</ListItemButton>
						<ListItemButton
							component="a"
							href="https://debank.com/profile/0xa7a0579fe6d6f258106c8d5d3830c3ec913b3296"
							target="_blank"
							rel="noreferrer"
						>
							<ListItemIcon>
								<Bank size={30} />
							</ListItemIcon>
							<ListItemText>{i18n.t("sidebar.treasury")}</ListItemText>
						</ListItemButton>
						<ListItemButton
							component="a"
							href="https://merch.vapornodes.finance/"
							target="_blank"
							rel="noreferrer"
						>
							<ListItemIcon>
								<Bag size={30} />
							</ListItemIcon>
							<ListItemText>
								{i18n.t("sidebar.merchStore")}
								{AUTO_REMOVE_SWAGSHOP_NEW_TAG - Date.now() >= 0 && (
									<Chip
										label="new"
										color="primary"
										size="small"
										sx={{ marginLeft: 2 }}
									/>
								)}
							</ListItemText>
						</ListItemButton>
					</List>
					<Stack
						direction="row"
						gap={2}
						margin={2}
						justifyContent={"center"}
						alignItems={"center"}
					>
						<Link href={defaultConfig.social.discord} passHref legacyBehavior>
							<IconButton component="a" target="_blank">
								<FontAwesomeIcon icon={faDiscord} />
							</IconButton>
						</Link>
						<Link href={defaultConfig.social.twitter} passHref legacyBehavior>
							<IconButton component="a" target="_blank">
								<FontAwesomeIcon icon={faTwitter} />
							</IconButton>
						</Link>
						<Link href={defaultConfig.social.youtube} passHref legacyBehavior>
							<IconButton component="a" target="_blank">
								<FontAwesomeIcon icon={faYoutube} />
							</IconButton>
						</Link>
					</Stack>
				</Box>
			</Box>
		</Drawer>
	);
};

export default Sidebar;
